import React, { FocusEvent, useState } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { eventTargetsHelpText } from '../../../helpers/forms/helpers';
import { applyComponentTheme } from '../../../theme';
import { GridItemProps } from '../../Grid';
import {
  defaultContainerId,
  defaultErrorId,
  defaultExplanatoryTextId,
  defaultLabelId,
  defaultTooltipId,
  defaultTooltipToggleId,
} from '../idHelpers';
import { InputReactElement } from '../inputTypes';
import ErrorContainer from './ErrorContainer';
import FieldGrid from './FieldGrid';
import FieldInfo, {
  ExplanatoryText,
  Label,
  Legend,
  Tooltip,
  TooltipProps,
} from './FieldInfo';

export type FieldProps = {
  children: InputReactElement;
  errorText?: string;
  warningText?: string;
  label: string;
  tooltip?: TooltipProps;
  alertBody?: string;
  explanatoryText?: string;
  fieldGridItemProps?: GridItemProps;
  alignLeft?: boolean;
  labelComponentProps?: ComponentProps;
} & ComponentProps;

const Field: React.FC<FieldProps> = ({
  children,
  errorText,
  warningText,
  label,
  tooltip,
  alertBody,
  explanatoryText,
  fieldGridItemProps,
  alignLeft,
  labelComponentProps,
  ...rest
}) => {
  /* Some fields, e.g. Radios and Dates have multiple inputs within them. In these cases we should
  render as a fieldset rather than a label to group those inputs together. */
  const useFieldSet = children.type.isFieldSet;

  const childId = children.props.id;
  const errorId = childId && defaultErrorId(childId);
  const containerId = childId && defaultContainerId(childId);
  const labelId = childId && defaultLabelId(childId);
  const tooltipId = childId && defaultTooltipId(childId);
  const tooltipToggleId = childId && defaultTooltipToggleId(childId);
  const explanatoryTextId = childId && defaultExplanatoryTextId(childId);

  const labelProps = labelComponentProps && componentProps(labelComponentProps);

  const [delayedBlurEvent, setDelayedBlurEvent] = useState<FocusEvent | null>(null);

  const targetsFromSameFieldset = (
    delayedEventTarget: EventTarget & Element,
    currentEventTarget: EventTarget | null
  ): boolean => {
    const getFieldsetId = (fieldId: string): string =>
      fieldId.substring(fieldId.indexOf('.') + 1, fieldId.indexOf('-'));

    return currentEventTarget instanceof HTMLInputElement
      ? getFieldsetId(delayedEventTarget.id) === getFieldsetId(currentEventTarget.id)
      : false;
  };

  const onToolTipClickOutside = (event: MouseEvent): void => {
    if (
      delayedBlurEvent &&
      !targetsFromSameFieldset(delayedBlurEvent.target, event.target)
    ) {
      if (children.props.onBlur && !!event.target) {
        children.props.onBlur({
          ...delayedBlurEvent,
          target: event.target as EventTarget & Element,
        });
        setDelayedBlurEvent(null);
      }
    }
  };

  return (
    <ErrorContainer
      errorText={errorText}
      warningText={warningText}
      {...componentProps(rest)}
      fieldSet={useFieldSet}
      id={containerId}
      errorId={errorId}>
      <FieldGrid alignLeft={alignLeft} gridItemProps={fieldGridItemProps}>
        <FieldInfo alertBody={alertBody}>
          {useFieldSet ? (
            <Legend id={labelId} {...labelProps}>
              {label}
            </Legend>
          ) : (
            <Label id={labelId} htmlFor={childId} {...labelProps}>
              {label}
            </Label>
          )}
          {explanatoryText && (
            <ExplanatoryText id={explanatoryTextId}>{explanatoryText}</ExplanatoryText>
          )}
          {tooltip && (
            <Tooltip
              {...tooltip}
              id={tooltipId}
              tooltipToggleId={tooltipToggleId}
              onClickOutside={onToolTipClickOutside}
            />
          )}
        </FieldInfo>
        {React.cloneElement(children, {
          'aria-describedby': `${
            errorText || warningText ? errorId : ''
          } ${explanatoryTextId}`,
          'aria-invalid': !!errorText,
          onBlur: (event: FocusEvent): void => {
            if (eventTargetsHelpText(event, children.props.id)) {
              setDelayedBlurEvent(event);
            } else {
              setDelayedBlurEvent(null);
              if (children.props.onBlur) {
                children.props.onBlur(event);
              }
            }
          },
        })}
      </FieldGrid>
    </ErrorContainer>
  );
};

export default applyComponentTheme(Field, 'Field');
